import { FC, useCallback, useContext, useEffect, useState } from 'react';

import './Events.css';
import {
  ApiError,
  BoothEvent,
  EventInput,
  EventSummary,
  EventsOverview,
  assignUnassignedSales,
  createEvent,
  deleteEvent,
  fetchEvents,
  updateEvent,
} from '../../api/client';
import { EventContext } from '../../api/eventContext';
import { formatDollars } from '../../shared/functions/formatter';
import {
  Button,
  Card,
  CardTitle,
  DangerZone,
  Dialog,
  EmptyState,
  Field,
  FormGrid,
  Input,
  Meta,
  Notice,
  SectionHeader,
  Select,
  Stack,
  StatusBadge,
  Switch,
  Textarea,
} from '../../ui';

type EventsProps = {
  onUnauthorized: () => void;
  /** Tell App to reload the nav's event list after a write. */
  onEventsChanged: () => void;
  /** Jump to the sales report scoped to one event, or to Unassigned. */
  onOpenSales: (focus: { eventId?: number; unassigned?: boolean } | null) => void;
};

type EventFormState = {
  name: string;
  start_date: string;
  end_date: string;
  venue: string;
  notes: string;
};

type Status = 'running' | 'upcoming' | 'past' | 'archived';

const STATUS_LABELS: Record<Status, string> = {
  running: 'running',
  upcoming: 'upcoming',
  past: 'past',
  archived: 'archived',
};

/**
 * The one event lifecycle, mapped onto the shared badge's tones: a booth taking
 * money now is positive, one still ahead is informational, and past/archived
 * booths are both quiet.
 */
const STATUS_TONES: Record<Status, 'success' | 'info' | 'neutral'> = {
  running: 'success',
  upcoming: 'info',
  past: 'neutral',
  archived: 'neutral',
};

/**
 * Today as the operator's calendar sees it.
 *
 * The date fields are local calendar dates, so "today" must be local too.
 * Reading the UTC date here classifies an event that starts today as
 * "upcoming" during the first hours of the morning east of UTC.
 */
function todayIso(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return now.getFullYear() + '-' + month + '-' + day;
}

function emptyForm(): EventFormState {
  return { name: '', start_date: todayIso(), end_date: '', venue: '', notes: '' };
}

/** "2026-09-16" -> "16 Sep 2026". Parsed as local since it is a calendar date. */
function formatDate(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatRange(event: BoothEvent): string {
  const start = formatDate(event.start_date);
  if (!event.end_date || event.end_date === event.start_date) {
    return start;
  }
  return start + ' – ' + formatDate(event.end_date);
}

function statusOf(event: BoothEvent): Status {
  if (event.is_archived) return 'archived';
  const today = todayIso();
  if (event.start_date > today) return 'upcoming';
  if (event.end_date !== null && event.end_date < today) return 'past';
  return 'running';
}

const Events: FC<EventsProps> = ({ onUnauthorized, onEventsChanged, onOpenSales }) => {
  const { activeEvent, setActiveEvent } = useContext(EventContext);

  const [overview, setOverview] = useState<EventsOverview | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [form, setForm] = useState<EventFormState>(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [pendingDelete, setPendingDelete] = useState<EventSummary | null>(null);
  const [reload, setReload] = useState(0);
  const [assignTarget, setAssignTarget] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [pendingAssign, setPendingAssign] = useState<number | null>(null);

  const handleError = useCallback(
    (err: unknown, fallback: string) => {
      if (err instanceof ApiError && err.status === 401) {
        onUnauthorized();
        return;
      }
      setError(err instanceof Error ? err.message : fallback);
    },
    [onUnauthorized]
  );

  useEffect(() => {
    let cancelled = false;

    fetchEvents(includeArchived)
      .then((data) => {
        if (cancelled) return;
        setOverview(data);
        setError('');
      })
      .catch((err: unknown) => {
        if (!cancelled) handleError(err, 'Could not load booth events.');
      });

    return () => {
      cancelled = true;
    };
  }, [includeArchived, reload, handleError]);

  /** Refetch here and tell App, so the nav picker cannot drift from this page. */
  const refresh = useCallback(() => {
    setReload((count) => count + 1);
    onEventsChanged();
  }, [onEventsChanged]);

  /** Attribute every unassigned sale to the chosen event, then refetch. */
  const confirmAssign = useCallback(() => {
    if (pendingAssign === null) return;
    setAssigning(true);
    assignUnassignedSales(pendingAssign)
      .then(() => {
        setPendingAssign(null);
        setAssignTarget('');
        setError('');
        refresh();
      })
      .catch((err: unknown) => {
        setPendingAssign(null);
        handleError(err, 'Could not assign the unassigned sales.');
      })
      .finally(() => setAssigning(false));
  }, [pendingAssign, refresh, handleError]);

  const openCreate = () => {
    setForm(emptyForm());
    setEditingId(null);
    setFormOpen(true);
    setError('');
  };

  const openEdit = (event: EventSummary) => {
    setForm({
      name: event.name,
      start_date: event.start_date,
      end_date: event.end_date ?? '',
      venue: event.venue,
      notes: event.notes,
    });
    setEditingId(event.id);
    setFormOpen(true);
    setError('');
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setError('');
  };

  const submit = () => {
    const name = form.name.trim();
    if (!name) {
      setError('An event needs a name.');
      return;
    }
    if (!form.start_date) {
      setError('Pick a start date.');
      return;
    }
    if (form.end_date && form.end_date < form.start_date) {
      setError('The end date cannot be before the start date.');
      return;
    }

    const payload: EventInput = {
      name,
      start_date: form.start_date,
      end_date: form.end_date || null,
      venue: form.venue.trim(),
      notes: form.notes.trim(),
    };

    setSaving(true);
    const action = editingId === null ? createEvent(payload) : updateEvent(editingId, payload);
    action
      .then(() => {
        setForm(emptyForm());
        setEditingId(null);
        setFormOpen(false);
        setError('');
        refresh();
      })
      .catch((err: unknown) => handleError(err, 'Could not save the event.'))
      .finally(() => setSaving(false));
  };

  const toggleArchive = (event: EventSummary) => {
    setBusyId(event.id);
    setError('');
    updateEvent(event.id, { is_archived: !event.is_archived })
      .then(() => refresh())
      .catch((err: unknown) => handleError(err, 'Could not update the event.'))
      .finally(() => setBusyId(null));
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    setBusyId(pendingDelete.id);
    deleteEvent(pendingDelete.id)
      .then(() => {
        setPendingDelete(null);
        // The delete control lives in the edit panel, so closing it keeps the
        // page from pointing at an event that no longer exists.
        setFormOpen(false);
        setEditingId(null);
        setError('');
        refresh();
      })
      .catch((err: unknown) => {
        // Close the prompt first: the banner behind it carries the explanation.
        setPendingDelete(null);
        handleError(err, 'Could not delete the event.');
      })
      .finally(() => setBusyId(null));
  };

  const statsList = (stats: EventsOverview['unassigned']) => (
    <dl className="eventsStats">
      <div>
        <dt>Revenue</dt>
        <dd className="money">{formatDollars(stats.revenue)}</dd>
      </div>
      <div>
        <dt>Sales</dt>
        <dd>{stats.transactions}</dd>
      </div>
      <div>
        <dt>Items</dt>
        <dd>{stats.items_sold}</dd>
      </div>
      <div>
        <dt>Average</dt>
        <dd className="money">{formatDollars(stats.average_sale)}</dd>
      </div>
    </dl>
  );

  const editingEvent = editingId === null ? null : (overview?.events.find((event) => event.id === editingId) ?? null);
  const editingHasSales = (editingEvent?.stats.transactions ?? 0) > 0;

  return (
    <div className="events">
      <SectionHeader
        title="Events"
        description="Every sale is tracked under the booth it was rung up at."
        action={
          <Stack direction="row" gap={5} align="center">
            <Switch checked={includeArchived} label="Show archived" onChange={setIncludeArchived} />
            <Button variant="primary" onClick={openCreate}>
              + new event
            </Button>
          </Stack>
        }
      />

      {error && (
        <Notice variant="error" onDismiss={() => setError('')}>
          {error}
        </Notice>
      )}

      {formOpen && (
        <Card className="eventsForm">
          <CardTitle>{editingId === null ? 'New event' : 'Edit event'}</CardTitle>
          <FormGrid columns={2}>
            <Field label="Name" span="full">
              <Input
                value={form.name}
                maxLength={120}
                autoFocus
                placeholder="e.g. Saturday Makers Market"
                onChange={(changed) => setForm({ ...form, name: changed.target.value })}
              />
            </Field>
            <Field label="Starts">
              <Input type="date" value={form.start_date} onChange={(changed) => setForm({ ...form, start_date: changed.target.value })} />
            </Field>
            <Field label="Ends (optional)">
              <Input type="date" value={form.end_date} onChange={(changed) => setForm({ ...form, end_date: changed.target.value })} />
            </Field>
            <Field label="Venue (optional)" span="full">
              <Input
                value={form.venue}
                maxLength={160}
                placeholder="e.g. Bayfront Pavilion, booth 12"
                onChange={(changed) => setForm({ ...form, venue: changed.target.value })}
              />
            </Field>
            <Field label="Notes (optional)" span="full">
              <Textarea
                value={form.notes}
                maxLength={2000}
                rows={2}
                placeholder="Anything worth remembering about this booth"
                onChange={(changed) => setForm({ ...form, notes: changed.target.value })}
              />
            </Field>
          </FormGrid>
          <Stack direction="row" gap={5} align="center" className="eventsFormActions">
            <Button variant="secondary" onClick={closeForm} disabled={saving}>
              cancel
            </Button>
            <Button variant="primary" onClick={submit} disabled={saving}>
              {saving ? 'saving…' : editingId === null ? 'create event' : 'save changes'}
            </Button>
          </Stack>
          {editingEvent && (
            <DangerZone
              className="eventsDanger"
              title="Delete event"
              description={
                editingHasSales
                  ? 'It has recorded sales, so it is kept for the reports and can only be archived.'
                  : 'It has no recorded sales, so nothing disappears from the reports.'
              }
              action={
                <Button
                  variant="destructive"
                  disabled={busyId !== null || editingHasSales}
                  title={editingHasSales ? 'This event has recorded sales. Archive it instead of deleting.' : 'Delete this empty event'}
                  onClick={() => setPendingDelete(editingEvent)}
                >
                  Delete event
                </Button>
              }
            />
          )}
        </Card>
      )}

      {!overview ? (
        <Meta>loading booth events…</Meta>
      ) : overview.events.length === 0 && overview.unassigned.transactions === 0 ? (
        <EmptyState title="No events yet" description="Create one to start tracking sales against it." />
      ) : (
        <>
          <div className="eventsGrid">
            {overview.events.map((event) => {
              const status = statusOf(event);
              const isActive = activeEvent?.id === event.id;
              return (
                <Card key={event.id} className={'eventsCard' + (status === 'archived' ? ' eventsCard--archived' : '')}>
                  <header className="eventsCardHead">
                    <div className="eventsCardTitleBlock">
                      <CardTitle className="eventsCardTitle">
                        {event.name}
                        {isActive && (
                          <StatusBadge className="eventsActiveTag" tone="info">
                            current
                          </StatusBadge>
                        )}
                      </CardTitle>
                      <Meta>
                        {formatRange(event)}
                        {event.venue ? ' · ' + event.venue : ''}
                      </Meta>
                    </div>
                    <StatusBadge className="eventsStatus" tone={STATUS_TONES[status]}>
                      {STATUS_LABELS[status]}
                    </StatusBadge>
                  </header>

                  {statsList(event.stats)}

                  {event.notes && <Meta className="eventsNotes">{event.notes}</Meta>}

                  <Stack direction="row" gap={2} className="eventsActions">
                    <Button variant="secondary" onClick={() => onOpenSales({ eventId: event.id })}>
                      view sales
                    </Button>
                    <Button
                      variant="secondary"
                      disabled={event.is_archived || isActive}
                      title={
                        event.is_archived ? 'Unarchive this event to sell under it.' : isActive ? 'Already the current event.' : undefined
                      }
                      onClick={() => setActiveEvent(event)}
                    >
                      use at till
                    </Button>
                    <Button variant="secondary" onClick={() => openEdit(event)}>
                      edit
                    </Button>
                    <Button variant="secondary" disabled={busyId === event.id} onClick={() => toggleArchive(event)}>
                      {event.is_archived ? 'unarchive' : 'archive'}
                    </Button>
                  </Stack>
                </Card>
              );
            })}
          </div>

          {overview.unassigned.transactions > 0 && (
            <Card className="eventsCard eventsUnassigned">
              <header className="eventsCardHead">
                <div className="eventsCardTitleBlock">
                  <CardTitle className="eventsCardTitle">Unassigned</CardTitle>
                  <Meta>Sales recorded before a booth event was chosen.</Meta>
                </div>
              </header>
              {statsList(overview.unassigned)}
              <Stack direction="row" gap={2} className="eventsActions">
                <Button variant="secondary" onClick={() => onOpenSales({ unassigned: true })}>
                  view sales
                </Button>
                {overview.events.length > 0 && (
                  <>
                    <span className="eventsAssign">
                      <Select
                        aria-label="Assign unassigned sales to"
                        value={assignTarget}
                        onChange={(changed) => setAssignTarget(changed.target.value)}
                      >
                        <option value="">assign to…</option>
                        {overview.events.map((event) => (
                          <option key={event.id} value={event.id}>
                            {event.name}
                          </option>
                        ))}
                      </Select>
                    </span>
                    <Button
                      variant="secondary"
                      disabled={assignTarget === '' || assigning}
                      onClick={() => setPendingAssign(Number(assignTarget))}
                    >
                      assign all
                    </Button>
                  </>
                )}
              </Stack>
            </Card>
          )}
        </>
      )}

      <Dialog
        open={pendingAssign !== null}
        onOpenChange={(open) => {
          if (!open) setPendingAssign(null);
        }}
        title="Assign unassigned sales?"
        description={
          overview ? (
            <>
              <strong>{overview.unassigned.transactions}</strong> {overview.unassigned.transactions === 1 ? 'sale' : 'sales'} with no event
              will be attributed to <strong>{overview.events.find((event) => event.id === pendingAssign)?.name}</strong>. Sales that already
              have an event are not touched.
            </>
          ) : (
            ''
          )
        }
        actions={
          <>
            <Button variant="secondary" onClick={() => setPendingAssign(null)} disabled={assigning}>
              cancel
            </Button>
            <Button variant="primary" onClick={confirmAssign} disabled={assigning}>
              {assigning ? 'assigning…' : 'assign sales'}
            </Button>
          </>
        }
      />

      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        title={pendingDelete ? 'Delete ' + pendingDelete.name + '?' : 'Delete event?'}
        tone="danger"
        description={
          pendingDelete ? (
            <>
              <strong>{pendingDelete.name}</strong> has no recorded sales, so nothing disappears from the reports.
            </>
          ) : (
            ''
          )
        }
        actions={
          <>
            <Button variant="secondary" onClick={() => setPendingDelete(null)} disabled={busyId !== null}>
              cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={busyId !== null}>
              {busyId !== null ? 'deleting…' : 'delete event'}
            </Button>
          </>
        }
      />
    </div>
  );
};

export default Events;
